import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SuccessStories = () => {
  const [activeStory, setActiveStory] = useState(0);

  const successStories = [
    {
      id: 1,
      category: "Arbeitsrecht",
      title: "Erfolgreiche Kündigungsschutzklage",
      challenge: "Unberechtigte Kündigung nach 8 Jahren Betriebszugehörigkeit",
      solution: `Sofortige telefonische Erstberatung innerhalb von 15 Minuten nach Anruf.\nUmfassende Analyse der Kündigungsgründe und rechtlichen Optionen.\nErstellung einer fundierten Kündigungsschutzklage mit detaillierter Begründung.`,
      outcome: "Vollständige Wiedereinstellung mit Nachzahlung aller Körper",
      timeframe: "3 Monate",
      satisfaction: "100%",
      icon: "Briefcase",
      color: "bg-trust-builder"
    },
    {
      id: 2,
      category: "Urheberrecht",
      title: "Verteidigung gegen Filesharing-Abmahnung",
      challenge: "Verbraucher erhielt Abmahnung wegen angeblicher Urheberrechtsverletzung durch Filesharing mit Schadensersatzforderung von 800€",
      solution: `Sofortige rechtliche Bewertung der Abmahnung und Prüfung auf Berechtigung.\nAnalyse der technischen Beweise und IP-Adresse-Zuordnung.\nAußergerichtliche Verhandlung mit der Gegenseite zur Schadensbegrenzung.`,
      outcome: "Abweisung der Schadensersatzforderung, nur geringe Anwaltskosten zu zahlen",
      timeframe: "6 Wochen",
      satisfaction: "98%",
      icon: "Shield",
      color: "bg-primary"
    },
    {
      id: 3,
      category: "Strafrecht",
      title: "Notfallberatung bei Durchsuchung",
      challenge: "Unerwartete Hausdurchsuchung mit Beschlagnahmung wichtiger Dokumente",
      solution: `Sofortige Notfallberatung noch während der Durchsuchung.\nAnwaltliche Begleitung vor Ort innerhalb von 2 Stunden.\nUmfassende Betreuung während des gesamten Ermittlungsverfahrens.`,
      outcome: "Verfahrenseinstellung ohne Anklageerhebung",
      timeframe: "4 Monate",
      satisfaction: "100%",
      icon: "Shield",
      color: "bg-conversion-accent"
    },
    {
      id: 4,
      category: "Immobilienrecht",
      title: "Mietrechtsstreitigkeit",
      challenge: "Unrechtmäßige Mieterhöhung und Kündigungsandrohung",
      solution: `Telefonische Sofortberatung zur rechtlichen Einschätzung.\nPrüfung der Mieterhöhung auf Rechtmäßigkeit und Höhe.\nAußergerichtliche Verhandlung mit dem Vermieter.`,
      outcome: "Rücknahme der Mieterhöhung und Verzicht auf Kündigung",
      timeframe: "2 Monate",
      satisfaction: "96%",
      icon: "Home",
      color: "bg-accent"
    }
  ];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Erfolgsgeschichten
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Anonymisierte Beispiele erfolgreicher Beratungen zeigen unsere Expertise 
            und das Vertrauen unserer Mandanten in verschiedenen Rechtsgebieten.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Story Navigation */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {successStories?.map((story, index) => (
                <motion.button
                  key={story?.id}
                  onClick={() => setActiveStory(index)}
                  className={`w-full text-left p-4 rounded-lg border transition-all duration-300 ${
                    activeStory === index
                      ? 'bg-white border-primary shadow-professional'
                      : 'bg-white border-border hover:border-primary/50'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`flex items-center justify-center w-10 h-10 ${story?.color} rounded-lg`}>
                      <Icon name={story?.icon} size={20} color="white" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-primary mb-1">
                        {story?.category}
                      </div>
                      <div className="text-sm text-text-primary font-semibold">
                        {story?.title}
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Story Details */}
          <div className="lg:col-span-2">
            <motion.div
              key={activeStory}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl shadow-professional p-8 border border-slate-200"
            >
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center space-x-4">
                  <div className={`flex items-center justify-center w-16 h-16 ${successStories?.[activeStory]?.color} rounded-xl`}>
                    <Icon name={successStories?.[activeStory]?.icon} size={28} color="white" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-primary mb-1">
                      {successStories?.[activeStory]?.category}
                    </div>
                    <h3 className="text-2xl font-bold text-text-primary">
                      {successStories?.[activeStory]?.title}
                    </h3>
                  </div>
                </div>

                {/* Challenge */}
                <div>
                  <h4 className="flex items-center space-x-2 text-lg font-semibold text-text-primary mb-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning" />
                    <span>Herausforderung</span>
                  </h4>
                  <p className="text-text-secondary leading-relaxed">
                    {successStories?.[activeStory]?.challenge}
                  </p>
                </div>

                {/* Solution */}
                <div>
                  <h4 className="flex items-center space-x-2 text-lg font-semibold text-text-primary mb-3">
                    <Icon name="Lightbulb" size={20} className="text-accent" />
                    <span>Unsere Lösung</span>
                  </h4>
                  <div className="text-text-secondary leading-relaxed whitespace-pre-line">
                    {successStories?.[activeStory]?.solution}
                  </div>
                </div>

                {/* Outcome */}
                <div>
                  <h4 className="flex items-center space-x-2 text-lg font-semibold text-text-primary mb-3">
                    <Icon name="CheckCircle" size={20} className="text-trust-builder" />
                    <span>Ergebnis</span>
                  </h4>
                  <p className="text-text-secondary leading-relaxed">
                    {successStories?.[activeStory]?.outcome}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid md:grid-cols-1 gap-6 pt-6 border-t border-border">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                      <Icon name="Clock" size={20} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-sm text-text-secondary">Bearbeitungszeit</div>
                      <div className="text-lg font-semibold text-text-primary">
                        {successStories?.[activeStory]?.timeframe}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Werden Sie Teil unserer Erfolgsgeschichten
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Lassen Sie sich von unseren erfahrenen Rechtsanwälten beraten und profitieren 
              von unserer bewährten Expertise in verschiedenen Rechtsgebieten.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-white/90 transition-colors duration-200 cta-button">
                Kostenlose Erstberatung
              </button>
              <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary transition-colors duration-200">
                Mehr erfahren
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;